import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import type { ApiError } from '@/types/api.types';

import { fetchClientsByStatus } from '../api/clientsApi';
import type { ClientListItem, ClientMetrics } from '../types';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function isNewClient(client: ClientListItem, now: Date): boolean {
  // The list projection does not include createdAt, so "new" can only be
  // determined when the field is present (e.g. a freshly created client).
  if (!client.createdAt) return false;
  return now.getTime() - new Date(client.createdAt).getTime() <= THIRTY_DAYS_MS;
}

export function useClients() {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const activeQuery = useQuery<ClientListItem[], ApiError>({
    queryKey: ['clients', accountId, 'ACTIVE'],
    queryFn: () => fetchClientsByStatus(accountId, 'ACTIVE'),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });

  const inactiveQuery = useQuery<ClientListItem[], ApiError>({
    queryKey: ['clients', accountId, 'INACTIVE'],
    queryFn: () => fetchClientsByStatus(accountId, 'INACTIVE'),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });

  const allClients = useMemo<ClientListItem[]>(
    () => [...(activeQuery.data ?? []), ...(inactiveQuery.data ?? [])],
    [activeQuery.data, inactiveQuery.data],
  );

  const metrics = useMemo<ClientMetrics>(() => {
    const now = new Date();
    const newCount = allClients.filter(
      (c) => c.status === 'ACTIVE' && isNewClient(c, now),
    ).length;
    const activeCount = allClients.filter(
      (c) => c.status === 'ACTIVE' && !isNewClient(c, now),
    ).length;
    const inactiveCount = allClients.filter((c) => c.status === 'INACTIVE').length;
    return {
      total: allClients.length,
      active: activeCount,
      newClients: newCount,
      inactive: inactiveCount,
    };
  }, [allClients]);

  return {
    allClients,
    metrics,
    isLoading: activeQuery.isLoading || inactiveQuery.isLoading,
    error: activeQuery.error ?? inactiveQuery.error ?? null,
  };
}
